/** Type-aware inline value editor for a single schema property.
 *
 * Ported (behaviour-preserving) from the `ValueCell` in
 * `../AssistedConfigEditor.tsx`, re-parameterized to take a `SchemaProp`
 * directly instead of a `ConfigRow`. The TO-config-specific
 * `auth_methods`/`client_auth_methods` special case is intentionally omitted.
 */
import {
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useState } from 'react';

import { enumLabel, safePreview } from './schemaOps';
import type { SchemaProp } from './types';

// ---------------------------------------------------------------------------
// Chip array cell
// ---------------------------------------------------------------------------

function ChipArrayCell({
  value,
  onChange,
  readOnly,
  enumValues,
  stringItems,
}: {
  value: unknown[];
  onChange: (v: unknown[]) => void;
  readOnly: boolean;
  enumValues?: unknown[];
  /** When the array's items are typed `string`, keep entries verbatim (don't
   *  coerce numeric-looking input to a number). */
  stringItems?: boolean;
}) {
  const [input, setInput] = useState('');

  if (enumValues) {
    const selected = (Array.isArray(value) ? value : []) as string[];
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {enumValues.map((ev) => {
          const s = String(ev);
          const checked = selected.includes(s);
          return (
            <Chip
              key={s}
              label={s}
              size="small"
              color={checked ? 'primary' : 'default'}
              onClick={
                readOnly
                  ? undefined
                  : () =>
                      onChange(
                        checked ? selected.filter((x) => x !== s) : [...selected, s],
                      )
              }
              variant={checked ? 'filled' : 'outlined'}
            />
          );
        })}
      </Box>
    );
  }

  const arr = Array.isArray(value) ? value : [];
  function add() {
    const v = input.trim();
    if (!v) return;
    if (stringItems) {
      // String-typed arrays keep values verbatim — numeric-looking custom-field
      // IDs must stay strings.
      onChange([...arr, v]);
    } else {
      // Otherwise coerce numeric-looking input to a number.
      const num = Number(v);
      onChange([...arr, Number.isNaN(num) ? v : num]);
    }
    setInput('');
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
      {arr.map((item, i) => (
        <Chip
          key={i}
          label={String(item)}
          size="small"
          onDelete={readOnly ? undefined : () => onChange(arr.filter((_, j) => j !== i))}
        />
      ))}
      {!readOnly && (
        <TextField
          size="small"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add();
            }
          }}
          placeholder="add…"
          sx={{ '& input': { py: 0.25, px: 0.75, width: 72, fontSize: 12 } }}
          variant="standard"
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Secret (masked) string field
// ---------------------------------------------------------------------------

function SecretStringCell({
  value,
  onChange,
  readOnly,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  readOnly: boolean;
}) {
  const [reveal, setReveal] = useState(false);
  const str = String(value ?? '');
  return (
    <TextField
      value={str}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      type={reveal ? 'text' : 'password'}
      disabled={readOnly}
      sx={{
        width: '100%',
        maxWidth: 720,
        '& input': { py: 0.5, fontSize: 13 },
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => setReveal((r) => !r)}
              edge="end"
              tabIndex={-1}
              aria-label={reveal ? 'Hide value' : 'Reveal value'}
            >
              {reveal ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Value cell — per-type dispatcher
// ---------------------------------------------------------------------------

export interface ValueCellProps {
  prop: SchemaProp | null;
  value: unknown;
  onChange: (v: unknown) => void;
  readOnly?: boolean;
}

/** Render the appropriate inline editor for `prop`'s type.
 *  - boolean → Switch
 *  - enum (string/number/integer) → Select
 *  - integer/number → number TextField
 *  - string → TextField (multiline when long; masked when `x-secret`)
 *  - array → chip-array cell
 *  A null/objectless schema, or an object type, renders a safe JSON preview. */
export function ValueCell({ prop, value, onChange, readOnly = false }: ValueCellProps) {
  // Unknown rows (no schema) render a read-only preview.
  if (!prop) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontFamily: 'monospace' }}
      >
        {safePreview(value)}
      </Typography>
    );
  }

  const type = prop.type;
  const enumVals = prop.enum;

  // Boolean — Switch
  if (type === 'boolean') {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            size="small"
            disabled={readOnly}
          />
        }
        label={
          <Typography variant="caption" color="text.secondary">
            {value ? 'true' : 'false'}
          </Typography>
        }
        sx={{ m: 0 }}
      />
    );
  }

  // Integer/number/string with enum — Select
  if ((type === 'integer' || type === 'number' || type === 'string') && enumVals) {
    return (
      <Select
        value={value ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          onChange(type === 'integer' ? Number(v) : v);
        }}
        size="small"
        disabled={readOnly}
        sx={{
          width: '100%',
          maxWidth: 420,
          fontSize: 13,
          '& .MuiSelect-select': { py: 0.5 },
        }}
      >
        {enumVals.map((ev) => (
          <MenuItem key={String(ev)} value={ev as string | number}>
            {enumLabel(prop, ev) ?? String(ev)}
          </MenuItem>
        ))}
      </Select>
    );
  }

  // Integer — number input
  if (type === 'integer') {
    return (
      <TextField
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        size="small"
        disabled={readOnly}
        inputProps={{ step: 1 }}
        sx={{
          width: '100%',
          maxWidth: 240,
          '& input': { py: 0.5, fontSize: 13 },
        }}
      />
    );
  }

  // Number (float)
  if (type === 'number') {
    return (
      <TextField
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        size="small"
        disabled={readOnly}
        inputProps={{ step: 0.01 }}
        sx={{
          width: '100%',
          maxWidth: 240,
          '& input': { py: 0.5, fontSize: 13 },
        }}
      />
    );
  }

  // String
  if (type === 'string') {
    if (prop['x-secret']) {
      return <SecretStringCell value={value} onChange={onChange} readOnly={readOnly} />;
    }
    const str = String(value ?? '');
    return (
      <TextField
        value={str}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        disabled={readOnly}
        multiline={str.length > 80}
        maxRows={6}
        sx={{
          width: '100%',
          maxWidth: 720,
          '& input, & textarea': { py: 0.5, fontSize: 13 },
        }}
      />
    );
  }

  // Array — enum lives on the items for arrays (e.g. booking_states). A scalar
  // stored under an array-typed key (e.g. a legacy single custom-field id) is
  // shown as a one-item array so it isn't silently dropped when editing.
  //
  // `prop.items` also catches scalar-or-list union types (e.g.
  // `["string","integer","array"]`) — declaring `items` marks the field as
  // list-like. Plain scalars are handled by the earlier branches, so a field
  // only reaches here as an array/union.
  if (type === 'array' || prop.items) {
    const arrValue = Array.isArray(value) ? value : value != null && value !== '' ? [value] : [];
    return (
      <ChipArrayCell
        value={arrValue}
        onChange={onChange}
        readOnly={readOnly}
        enumValues={prop.items?.enum ?? enumVals}
        stringItems={prop.items?.type === 'string'}
      />
    );
  }

  // Object / null — schema-typed but not directly editable here. Show a safe
  // preview (nested objects are expanded as child rows by the grid instead).
  return (
    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
      {safePreview(value)}
    </Typography>
  );
}

// ---------------------------------------------------------------------------
// Labelled wrapper — for wizard/form usage
// ---------------------------------------------------------------------------

export interface SchemaValueFieldProps {
  prop: SchemaProp;
  /** Field label. Defaults to nothing if omitted. */
  label?: string;
  value: unknown;
  onChange: (v: unknown) => void;
  readOnly?: boolean;
}

/** A labelled wrapper around {@link ValueCell}: renders `label`, the prop's
 *  `description` as helper text, and the type-aware value editor beneath. */
export function SchemaValueField({
  prop,
  label,
  value,
  onChange,
  readOnly = false,
}: SchemaValueFieldProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {label && (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
      )}
      <ValueCell prop={prop} value={value} onChange={onChange} readOnly={readOnly} />
      {prop.description && (
        <Typography variant="caption" color="text.secondary">
          {prop.description}
        </Typography>
      )}
    </Box>
  );
}
